import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, LoadingSpinner, RefreshButton } from '../components/ui';
import usePageTitle from '../hooks/usePageTitle';
import useSmartRefresh from '../hooks/useSmartRefresh';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { toast } from 'react-hot-toast';

/**
 * IMEI Check Page
 */
const IMEICheckPage = () => {
  // Set page title
  usePageTitle('IMEI Check');

  // Tab state
  const [activeTab, setActiveTab] = useState('single');

  // Single check state
  const [imei, setImei] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Bulk check state
  const [bulkImeis, setBulkImeis] = useState('');
  const [isBulkChecking, setIsBulkChecking] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkError, setBulkError] = useState('');
  const [bulkImeiCount, setBulkImeiCount] = useState(0);
  const [bulkProgress, setBulkProgress] = useState({
    current: 0,
    total: 0,
    percentage: 0,
    successful: 0,
    failed: 0,
    cached: 0,
  });

  // Common state
  const [services, setServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [loadingServices, setLoadingServices] = useState(true);
  const [showAllRecords, setShowAllRecords] = useState(false);

  // History state
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all'); // 'all', 'single', 'bulk'
  const [expandedBatches, setExpandedBatches] = useState(new Set());

  // Fetch services on mount
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const { default: imeiService } =
          await import('../services/imeiService');
        const data = await imeiService.getServices();

        // Handle paginated or array response
        const servicesList = data.results || data;
        setServices(servicesList);

        // Set default service (Basic IMEI Check or first available)
        if (servicesList.length > 0) {
          const defaultService =
            servicesList.find((s) => s.title === 'Basic IMEI Check') ||
            servicesList[0];
          setSelectedServiceId(defaultService.service_id);
        }
      } catch (err) {
        console.error('Error fetching services:', err);
        toast.error('Failed to load IMEI services');
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, []);

  const { isRefreshing, refreshData } = useSmartRefresh(
    () => {}, // Placeholder for refresh logic if needed
    'IMEI Check data refreshed',
  );

  const handleIMEIChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only numbers
    if (value.length <= 15) {
      setImei(value);
      setError('');
    }
  };

  const validateIMEI = (imeiNumber) => {
    if (imeiNumber.length !== 15) return false;

    // Luhn algorithm for IMEI validation
    let sum = 0;
    for (let i = 0; i < 14; i++) {
      let digit = parseInt(imeiNumber[i]);
      if (i % 2 === 1) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    return checkDigit === parseInt(imeiNumber[14]);
  };

  const handleCheck = async () => {
    if (!imei) {
      setError('Please enter an IMEI number');
      return;
    }

    if (!validateIMEI(imei)) {
      setError('Invalid IMEI number format');
      return;
    }

    setIsChecking(true);
    setError('');

    try {
      // Import imei service
      const { default: imeiService } = await import('../services/imeiService');

      // Perform IMEI check through imei service
      const response = await imeiService.checkIMEI(imei, selectedServiceId);

      if (response.success) {
        setResult({
          imei: response.data.imei,
          brand: response.data?.brand || 'Unknown',
          model: response.data?.model || 'Unknown',
          status: response.data?.status || 'Unknown',
          carrier: response.data?.carrier || 'Unknown',
          country: response.data?.country || 'Unknown',
          warranty: response.data?.warranty || 'Unknown',
          blacklisted: response.data?.blacklisted || false,
          stolen: response.data?.stolen || false,
          checkDate: new Date().toLocaleDateString(),
          cost: response.cost,
          wasCached: response.was_cached,
        });

        // Show success message with caching info
        if (response.was_cached) {
          toast.success('IMEI check completed from cache (No cost applied)');
        } else {
          toast.success(`IMEI check completed. Cost: $${response.cost}`);
        }
      } else {
        setError(response.message || 'Failed to check IMEI. Please try again.');
      }
    } catch (err) {
      console.error('IMEI check error:', err);
      setError(err.message || 'Failed to check IMEI. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  const handleNewCheck = () => {
    setImei('');
    setResult(null);
    setError('');
    // Refresh history after new check
    fetchHistory();
  };

  // Fetch history
  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const { default: imeiService } = await import('../services/imeiService');

      // Fetch history (batches info is derived from individual orders)
      const historyResponse = await imeiService.getHistory();

      // Handle both paginated ({results: []}) and non-paginated ([]) responses
      const historyData = historyResponse.results || historyResponse;
      if (Array.isArray(historyData)) {
        setHistory(historyData);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
      toast.error('Failed to load history');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch history on mount and after checks
  useEffect(() => {
    if (selectedServiceId) {
      fetchHistory();
    }
  }, [selectedServiceId]);

  // Toggle batch expansion
  const toggleBatchExpansion = (batchId) => {
    setExpandedBatches((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });
  };

  // Bulk check handlers
  const handleBulkImeisChange = (e) => {
    const value = e.target.value;
    setBulkImeis(value);
    setBulkError('');

    // Count valid IMEIs
    const lines = value
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    setBulkImeiCount(lines.length);
  };

  const handleBulkCheck = async () => {
    if (bulkImeiCount === 0) {
      setBulkError('Please enter at least one IMEI number');
      return;
    }

    // Parse and validate IMEIs
    const imeiList = bulkImeis
      .split('\n')
      .map((line) => line.trim().replace(/\D/g, ''))
      .filter((line) => line.length > 0);

    // Validate each IMEI
    const invalidImeis = imeiList.filter((imei) => imei.length !== 15);
    if (invalidImeis.length > 0) {
      setBulkError(
        `${invalidImeis.length} IMEI(s) are invalid. Each IMEI must be exactly 15 digits.`,
      );
      return;
    }

    setIsBulkChecking(true);
    setBulkError('');

    // Initialize progress state
    setBulkProgress({
      current: 0,
      total: imeiList.length,
      percentage: 0,
      successful: 0,
      failed: 0,
      cached: 0,
    });

    try {
      const { default: imeiService } = await import('../services/imeiService');

      // Step 1: Initialize batch
      toast.loading('Initializing bulk check...');
      const batchResponse = await imeiService.initBatch(selectedServiceId, {
        name: `Bulk Check - ${new Date().toLocaleString()}`,
        total_imeis: imeiList.length,
      });

      if (!batchResponse.success) {
        throw new Error(batchResponse.message || 'Failed to initialize batch');
      }

      const batchId = batchResponse.batch_id;
      toast.dismiss();
      toast.success('Batch initialized! Processing IMEIs...');

      // Step 2: Process each IMEI sequentially
      const results = [];
      let successCount = 0;
      let failCount = 0;
      let cachedCount = 0;

      for (let i = 0; i < imeiList.length; i++) {
        try {
          const result = await imeiService.checkIMEI(
            imeiList[i],
            selectedServiceId,
            batchId,
          );

          if (result.success) {
            successCount++;
            if (result.was_cached) {
              cachedCount++;
            }
            results.push({ success: true, ...result });
          } else {
            failCount++;
            results.push({
              success: false,
              imei: imeiList[i],
              error: result.message,
            });
          }

          // Update progress
          setBulkProgress({
            current: i + 1,
            total: imeiList.length,
            percentage: ((i + 1) / imeiList.length) * 100,
            successful: successCount,
            failed: failCount,
            cached: cachedCount,
          });
        } catch (error) {
          failCount++;
          results.push({
            success: false,
            imei: imeiList[i],
            error: error.message || 'Check failed',
          });

          // Update progress even on error
          setBulkProgress({
            current: i + 1,
            total: imeiList.length,
            percentage: ((i + 1) / imeiList.length) * 100,
            successful: successCount,
            failed: failCount,
            cached: cachedCount,
          });
        }
      }

      // Step 3: Finalize batch
      const summary = await imeiService.finalizeBatch(batchId);

      if (summary.success) {
        setBulkResult({
          total_count: summary.summary.total_count,
          successful_count: summary.summary.successful_count,
          failed_count: summary.summary.failed_count,
          cached_count: summary.summary.cached_count,
          total_cost: summary.summary.total_cost,
          batch_id: batchId,
        });

        toast.success(
          `Bulk check completed! ${summary.summary.successful_count}/${summary.summary.total_count} successful. Total cost: $${summary.summary.total_cost}`,
        );
      } else {
        throw new Error('Failed to finalize batch');
      }
    } catch (err) {
      console.error('Bulk IMEI check error:', err);
      setBulkError(
        err.message || 'Failed to process bulk check. Please try again.',
      );
      toast.error('Bulk check failed');
    } finally {
      setIsBulkChecking(false);
    }
  };

  const handleNewBulkCheck = () => {
    setBulkImeis('');
    setBulkResult(null);
    setBulkError('');
    setBulkImeiCount(0);
    // Refresh history after bulk check
    fetchHistory();
  };

  // Filter history based on selected filter
  const getFilteredHistory = () => {
    if (historyFilter === 'single') {
      return history.filter((order) => !order.batch);
    } else if (historyFilter === 'bulk') {
      // Return unique batches
      const batchMap = new Map();
      history.forEach((order) => {
        if (order.batch && !batchMap.has(order.batch)) {
          batchMap.set(order.batch, order);
        }
      });
      return Array.from(batchMap.values());
    }
    // 'all' - show both single orders and batch summaries
    const combined = [];
    const processedBatches = new Set();

    history.forEach((order) => {
      if (order.batch) {
        if (!processedBatches.has(order.batch)) {
          processedBatches.add(order.batch);
          combined.push({ ...order, isBatchSummary: true });
        }
      } else {
        combined.push(order);
      }
    });

    return combined;
  };

  const filteredHistory = getFilteredHistory();

  // Get batch orders for expansion
  const getBatchOrders = (batchId) => {
    return history.filter((order) => order.batch === batchId);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Show only first 4 records initially
  const displayedChecks = showAllRecords
    ? filteredHistory
    : filteredHistory.slice(0, 4);

  return (
    <DashboardLayout pageName="IMEI Check">
      <div className="space-y-8">
        {/* Header with Refresh Button */}
        <motion.div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-4 sm:mb-0">
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Check your device's IMEI number for detailed information and
              status
            </p>
          </div>
          <RefreshButton
            onRefresh={refreshData}
            isRefreshing={isRefreshing}
            tooltip="Refresh IMEI Check Data"
          />
        </motion.div>

        {/* Tabs and IMEI Input Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20 transition-colors duration-300">
            {/* Tabs Header */}
            <div className="flex space-x-1 mb-6 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('single')}
                className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  activeTab === 'single'
                    ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Single Check
              </button>
              <button
                onClick={() => setActiveTab('bulk')}
                className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  activeTab === 'bulk'
                    ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Bulk Check
              </button>
            </div>

            {/* Single Check Tab */}
            {activeTab === 'single' && (
              <div className="space-y-4">
                <div>
                  <Input
                    label="IMEI Number"
                    value={imei}
                    onChange={handleIMEIChange}
                    placeholder="Enter 15-digit IMEI number"
                    error={error}
                    disabled={isChecking}
                    className="text-lg font-mono"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Find your IMEI by dialing *#06# or in Settings → About Phone
                  </p>
                </div>

                {/* Service Selection Dropdown */}
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Service Type
                  </label>
                  <div className="relative">
                    <select
                      value={selectedServiceId || ''}
                      onChange={(e) =>
                        setSelectedServiceId(Number(e.target.value))
                      }
                      disabled={isChecking || loadingServices}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent dark:bg-gray-700 dark:text-white text-base appearance-none bg-white"
                    >
                      {loadingServices ? (
                        <option>Loading services...</option>
                      ) : (
                        services.map((service) => (
                          <option key={service.id} value={service.service_id}>
                            {service.title} - ${service.price}
                          </option>
                        ))
                      )}
                    </select>
                    {/* Custom Chevron Icon */}
                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <Button
                    variant="primary"
                    size="large"
                    onClick={handleCheck}
                    disabled={!imei || isChecking}
                    loading={isChecking}
                  >
                    {isChecking ? 'Checking...' : 'Check IMEI'}
                  </Button>

                  {result && (
                    <Button
                      variant="outline"
                      size="large"
                      onClick={handleNewCheck}
                    >
                      New Check
                    </Button>
                  )}
                </div>

                {/* Progress indicator */}
                {isChecking && (
                  <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
                    <LoadingSpinner size="small" />
                    <span>Checking IMEI database...</span>
                  </div>
                )}
              </div>
            )}

            {/* Bulk Check Tab */}
            {activeTab === 'bulk' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    IMEI Numbers (One per line)
                  </label>
                  <textarea
                    value={bulkImeis}
                    onChange={handleBulkImeisChange}
                    placeholder="Enter multiple IMEI numbers, one per line&#10;&#10;Example:&#10;123456789012345&#10;987654321098765&#10;456789012345678"
                    disabled={isBulkChecking}
                    rows={10}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent dark:bg-gray-700 dark:text-white text-base font-mono resize-none"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {bulkImeiCount > 0 ? (
                      <span className="font-medium text-primary-600 dark:text-primary-400">
                        {bulkImeiCount} IMEI{bulkImeiCount !== 1 ? 's' : ''}{' '}
                        entered
                      </span>
                    ) : (
                      'Enter one IMEI number per line'
                    )}
                  </p>
                  {bulkError && (
                    <p className="text-sm text-error-600 dark:text-error-400 mt-2">
                      {bulkError}
                    </p>
                  )}
                </div>

                {/* Service Selection for Bulk */}
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Service Type
                  </label>
                  <div className="relative">
                    <select
                      value={selectedServiceId || ''}
                      onChange={(e) =>
                        setSelectedServiceId(Number(e.target.value))
                      }
                      disabled={isBulkChecking || loadingServices}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent dark:bg-gray-700 dark:text-white text-base appearance-none bg-white"
                    >
                      {loadingServices ? (
                        <option>Loading services...</option>
                      ) : (
                        services.map((service) => (
                          <option key={service.id} value={service.service_id}>
                            {service.title} - ${service.price}
                          </option>
                        ))
                      )}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <Button
                    variant="primary"
                    size="large"
                    onClick={handleBulkCheck}
                    disabled={bulkImeiCount === 0 || isBulkChecking}
                    loading={isBulkChecking}
                  >
                    {isBulkChecking
                      ? 'Processing...'
                      : `Check ${bulkImeiCount} IMEI${bulkImeiCount !== 1 ? 's' : ''}`}
                  </Button>

                  {bulkResult && (
                    <Button
                      variant="outline"
                      size="large"
                      onClick={handleNewBulkCheck}
                    >
                      New Bulk Check
                    </Button>
                  )}
                </div>

                {/* Progress indicator for bulk */}
                {isBulkChecking && (
                  <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                      <div
                        className="bg-primary-600 dark:bg-primary-500 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${bulkProgress.percentage}%` }}
                      />
                    </div>

                    {/* Progress Text */}
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-gray-700 dark:text-gray-300">
                        Processing {bulkProgress.current} of{' '}
                        {bulkProgress.total} IMEIs...
                      </span>
                      <span className="text-primary-600 dark:text-primary-400">
                        {bulkProgress.percentage.toFixed(0)}%
                      </span>
                    </div>

                    {/* Live Stats */}
                    <div className="grid grid-cols-3 gap-4 text-sm pt-2 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-center">
                        <p className="text-gray-600 dark:text-gray-400 text-xs">
                          Successful
                        </p>
                        <p className="text-lg font-semibold text-success-600 dark:text-success-400">
                          {bulkProgress.successful}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-600 dark:text-gray-400 text-xs">
                          Failed
                        </p>
                        <p className="text-lg font-semibold text-error-600 dark:text-error-400">
                          {bulkProgress.failed}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-600 dark:text-gray-400 text-xs">
                          Cached
                        </p>
                        <p className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                          {bulkProgress.cached}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bulk Result Summary */}
                {bulkResult && (
                  <div className="mt-4 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/30 rounded-lg">
                    <h4 className="font-semibold text-primary-900 dark:text-primary-100 mb-2">
                      Bulk Check Summary
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 dark:text-gray-400">
                          Total
                        </p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {bulkResult.total_count}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 dark:text-gray-400">
                          Successful
                        </p>
                        <p className="text-lg font-semibold text-success-600 dark:text-success-400">
                          {bulkResult.successful_count}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 dark:text-gray-400">
                          Failed
                        </p>
                        <p className="text-lg font-semibold text-error-600 dark:text-error-400">
                          {bulkResult.failed_count}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 dark:text-gray-400">
                          Cached
                        </p>
                        <p className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                          {bulkResult.cached_count || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 dark:text-gray-400">
                          Total Cost
                        </p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          ${bulkResult.total_cost}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Results Section */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  IMEI Check Results
                </h2>
                <div
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    result.status === 'Clean'
                      ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-400'
                      : 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-400'
                  }`}
                >
                  {result.status}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Device Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Device Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        IMEI:
                      </span>
                      <span className="font-mono font-medium text-gray-900 dark:text-gray-100">
                        {result.imei}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Brand:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {result.brand}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Model:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {result.model}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Country:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {result.country}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Status Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Status Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Carrier:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {result.carrier}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Warranty:
                      </span>
                      <span
                        className={`font-medium ${result.warranty === 'Active' ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}
                      >
                        {result.warranty}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Blacklisted:
                      </span>
                      <span
                        className={`font-medium ${result.blacklisted ? 'text-error-600 dark:text-error-400' : 'text-success-600 dark:text-success-400'}`}
                      >
                        {result.blacklisted ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Stolen:
                      </span>
                      <span
                        className={`font-medium ${result.stolen ? 'text-error-600 dark:text-error-400' : 'text-success-600 dark:text-success-400'}`}
                      >
                        {result.stolen ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>Check performed on: {result.checkDate}</span>
                  <Button variant="outline" size="small">
                    Download Report
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Recent Checks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Recent Checks
              </h3>
              <div className="flex items-center space-x-2">
                {/* Filter Buttons */}
                <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                  <button
                    onClick={() => setHistoryFilter('all')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      historyFilter === 'all'
                        ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setHistoryFilter('single')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      historyFilter === 'single'
                        ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    Single
                  </button>
                  <button
                    onClick={() => setHistoryFilter('bulk')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      historyFilter === 'bulk'
                        ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    Bulk
                  </button>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={fetchHistory}
                  disabled={loadingHistory}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  title="Refresh history"
                >
                  <svg
                    className={`w-5 h-5 ${loadingHistory ? 'animate-spin' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>

                {filteredHistory.length > 4 && (
                  <button
                    onClick={() => setShowAllRecords(!showAllRecords)}
                    className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors duration-200"
                  >
                    {showAllRecords ? 'Show Less' : 'View All'}
                  </button>
                )}
              </div>
            </div>

            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="medium" />
              </div>
            ) : displayedChecks.length > 0 ? (
              <div className="space-y-3">
                {displayedChecks.map((check, index) => {
                  const isBatch = check.isBatchSummary || check.batch;
                  const batchOrders =
                    isBatch && check.batch ? getBatchOrders(check.batch) : [];
                  const isExpanded = expandedBatches.has(check.batch);

                  return (
                    <div key={check.id || index}>
                      <div
                        className={`flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50 ${
                          isBatch
                            ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
                            : ''
                        }`}
                        onClick={() =>
                          isBatch &&
                          check.batch &&
                          toggleBatchExpansion(check.batch)
                        }
                      >
                        <div className="flex-1">
                          {isBatch ? (
                            <>
                              <div className="flex items-center space-x-2">
                                <span className="px-2 py-1 text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded">
                                  📦 Bulk Batch
                                </span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                  {batchOrders.length} IMEIs
                                </p>
                              </div>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {formatDate(check.created_at)} at{' '}
                                {formatTime(check.created_at)}
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="font-mono text-sm text-gray-600 dark:text-gray-400">
                                {check.imei}
                              </p>
                              <p className="font-medium text-gray-900 dark:text-gray-100">
                                {check.result?.brand || 'Unknown'}{' '}
                                {check.result?.model || ''}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {formatDate(check.created_at)} at{' '}
                                {formatTime(check.created_at)}
                              </p>
                            </>
                          )}
                        </div>

                        <div className="flex items-center space-x-3">
                          {check.was_cached && (
                            <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                              Cached
                            </span>
                          )}
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${
                              check.status === 'completed'
                                ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                                : 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300'
                            }`}
                          >
                            {check.status === 'completed'
                              ? '✓ Success'
                              : '✗ Failed'}
                          </span>
                          {isBatch && check.batch && (
                            <svg
                              className={`w-5 h-5 text-gray-400 transition-transform ${
                                isExpanded ? 'rotate-180' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          )}
                        </div>
                      </div>

                      {/* Expanded Batch Items */}
                      {isBatch && isExpanded && batchOrders.length > 0 && (
                        <div className="ml-6 mt-2 space-y-2 border-l-2 border-primary-200 dark:border-primary-800 pl-4">
                          {batchOrders.map((order) => (
                            <div
                              key={order.id}
                              className="p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="font-mono text-sm text-gray-600 dark:text-gray-400">
                                    {order.imei}
                                  </p>
                                  <p className="text-sm text-gray-500 dark:text-gray-500">
                                    {order.result?.brand || 'Unknown'}{' '}
                                    {order.result?.model || ''}
                                  </p>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {order.was_cached && (
                                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                      Cached
                                    </span>
                                  )}
                                  <span
                                    className={`text-sm ${order.status === 'completed' ? 'text-success-600' : 'text-error-600'}`}
                                  >
                                    {order.status === 'completed' ? '✓' : '✗'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📱</div>
                <p className="text-gray-600 dark:text-gray-400">
                  No recent checks
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Your IMEI check history will appear here
                </p>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Card className="p-6 bg-primary-50 dark:bg-primary-900/10 border-primary-200 dark:border-primary-800/30">
            <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-100 mb-3">
              About IMEI Checks
            </h3>
            <div className="text-sm text-primary-800 dark:text-primary-200 space-y-2">
              <p>
                • IMEI (International Mobile Equipment Identity) is a unique
                15-digit number for every mobile device
              </p>
              <p>
                • Our database checks against global blacklists and carrier
                information
              </p>
              <p>
                • Results include device status, warranty information, and
                carrier lock status
              </p>
              <p>
                • All checks are logged in your account for future reference
              </p>
            </div>
          </Card>
        </motion.div>

        {/* All Records Modal */}
        <AnimatePresence>
          {showAllRecords && (
            <motion.div
              className="fixed inset-0 z-50 overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
                onClick={() => setShowAllRecords(false)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />

              <div className="flex items-center justify-center min-h-screen px-4 py-8">
                <motion.div
                  className="relative w-full max-w-5xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-colors duration-300"
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div>
                      <h2 className="text-2xl font-semibold text-primary-600 dark:text-primary-400">
                        All IMEI Check Records
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Complete history of your IMEI checks
                      </p>
                    </div>
                    <button
                      onClick={() => setShowAllRecords(false)}
                      className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-6 max-h-96 overflow-y-auto">
                    <div className="space-y-3">
                      {filteredHistory.map((check, index) => {
                        const isBatch = check.isBatchSummary || check.batch;
                        const batchOrders =
                          isBatch && check.batch
                            ? getBatchOrders(check.batch)
                            : [];
                        const isExpanded = expandedBatches.has(check.batch);

                        return (
                          <div key={check.id || index}>
                            <div
                              className={`flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50 ${
                                isBatch
                                  ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
                                  : ''
                              }`}
                              onClick={() =>
                                isBatch &&
                                check.batch &&
                                toggleBatchExpansion(check.batch)
                              }
                            >
                              <div className="flex-1">
                                {isBatch ? (
                                  <>
                                    <div className="flex items-center space-x-2">
                                      <span className="px-2 py-1 text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded">
                                        📦 Bulk Batch
                                      </span>
                                      <p className="font-medium text-gray-900 dark:text-gray-100">
                                        {batchOrders.length} IMEIs
                                      </p>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                      {formatDate(check.created_at)} at{' '}
                                      {formatTime(check.created_at)}
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <p className="font-mono text-sm text-gray-600 dark:text-gray-400">
                                      {check.imei}
                                    </p>
                                    <p className="font-medium text-gray-900 dark:text-gray-100">
                                      {check.result?.brand || 'Unknown'}{' '}
                                      {check.result?.model || ''}
                                    </p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                      {formatDate(check.created_at)} at{' '}
                                      {formatTime(check.created_at)}
                                    </p>
                                  </>
                                )}
                              </div>

                              <div className="flex items-center space-x-3">
                                {check.was_cached && (
                                  <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                    Cached
                                  </span>
                                )}
                                <span
                                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                                    check.status === 'completed'
                                      ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                                      : 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300'
                                  }`}
                                >
                                  {check.status === 'completed'
                                    ? '✓ Success'
                                    : '✗ Failed'}
                                </span>
                                {isBatch && check.batch && (
                                  <svg
                                    className={`w-5 h-5 text-gray-400 transition-transform ${
                                      isExpanded ? 'rotate-180' : ''
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                )}
                              </div>
                            </div>

                            {/* Expanded Batch Items */}
                            {isBatch &&
                              isExpanded &&
                              batchOrders.length > 0 && (
                                <div className="ml-6 mt-2 space-y-2 border-l-2 border-primary-200 dark:border-primary-800 pl-4">
                                  {batchOrders.map((order) => (
                                    <div
                                      key={order.id}
                                      className="p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <p className="font-mono text-sm text-gray-600 dark:text-gray-400">
                                            {order.imei}
                                          </p>
                                          <p className="text-sm text-gray-500 dark:text-gray-500">
                                            {order.result?.brand || 'Unknown'}{' '}
                                            {order.result?.model || ''}
                                          </p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          {order.was_cached && (
                                            <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                              Cached
                                            </span>
                                          )}
                                          <span
                                            className={`text-sm ${order.status === 'completed' ? 'text-success-600' : 'text-error-600'}`}
                                          >
                                            {order.status === 'completed'
                                              ? '✓'
                                              : '✗'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setShowAllRecords(false)}
                      className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                      Close
                    </button>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};

export default IMEICheckPage;
